import React, { CSSProperties } from "react";
import { BaseEntity } from "../../../../models/baseEntity";
import styled from "styled-components";
import { SearchBar } from "./SearchBar";
import noImage from "../../../../../resources/missing-img.png";
import { FaCheck, FaMinus, FaPlus } from "react-icons/fa6";
import { IconWrapper, ImageWrapper } from "./TableSettingsComponents";
import { CMSAsset } from "@client/models/CMS/cmsAsset";
import { CMSTag } from "@client/models/CMS/cmsTag";
import { CMSGenre } from "@client/models/CMS/cmsGenre";

const TableWrapper = styled.div`
  width: 100%;
  height: 500px;
`;

const TableSlice = styled.div`
  width: 49.75%;
  height: 100%;
  display: inline-block;
  vertical-align: top;
`;

const TableSliceHeader = styled.div`
  height: 15%;
  font-size: 18px;
  padding: 15px;
  background-color: rgba(33, 37, 41, 0.2);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TableSliceContent = styled.div`
  height: 85%;
  overflow: auto;
`;

const CustomSearchBar = styled(SearchBar)`
  display: inline-flex;
`;

const EntityRowWrapper = styled.div`
  &.disabled {
    background-color: rgba(33, 37, 41, 0.1);
  }

  width: 100%;
  height: 75px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: solid 1px rgba(33, 37, 41, 0.2);

  :hover {
    cursor: pointer;
  }
`;

const EntityText = styled.div`
  width: 80%;
  margin-left: 20px;
  div {
    text-align: left;
  }
`;

const CustomImageWrapper = styled(ImageWrapper)`
  margin: 0 0 0 10px;
`;

const CustomIconWrapper = styled(IconWrapper)`
  margin-right: 10px;
`;

interface EntityRowProps {
  entity: BaseEntity | CMSAsset | CMSGenre | CMSTag;
  onClick: () => void;
  isDisabled?: boolean;
  rightSide?: boolean;
  noImages?: boolean;
}

const EntityRow: React.FC<EntityRowProps> = (props) => {
  const {
    entity,
    onClick,
    isDisabled = false,
    rightSide = true,
    noImages = false,
  } = props;
  return (
    <EntityRowWrapper
      onClick={isDisabled ? undefined : onClick}
      className={isDisabled ? "disabled" : ""}
    >
      {!noImages && (
        // Rendering a large list of images will slow down rerendering
        <CustomImageWrapper>
          <div
            className="image"
            style={{
              backgroundImage: `url(${entity?.getImage() || noImage})`,
            }}
          />
        </CustomImageWrapper>
      )}
      <EntityText>
        <div>{entity?.getDisplayName()}</div>
      </EntityText>
      <CustomIconWrapper>
        {rightSide && !isDisabled && <FaPlus />}
        {rightSide && isDisabled && <FaCheck />}
        {!rightSide && <FaMinus />}
      </CustomIconWrapper>
    </EntityRowWrapper>
  );
};

interface EntitySelectionTableProps {
  label: string;
  values?: (BaseEntity | CMSAsset | CMSGenre | CMSTag)[];
  selectedValues?: (BaseEntity | CMSAsset | CMSGenre | CMSTag)[];
  noImages?: boolean;
  push: (entity: BaseEntity | CMSAsset | CMSGenre | CMSTag) => void;
  remove: (index: number) => void;
  searchFunction?: (query: string) => void;
  style?: CSSProperties;
}

export const EntitySelectionTable: React.FC<EntitySelectionTableProps> = (
  props
) => {
  const {
    label,
    values = [],
    selectedValues = [],
    noImages = false,
    push,
    remove,
    searchFunction,
    style,
  } = props;

  const searchFunc = (query: string) => {
    searchFunction && searchFunction(query);
  };

  const isSelected = (
    formValues: (BaseEntity | CMSAsset | CMSGenre | CMSTag)[],
    entity: BaseEntity | CMSAsset | CMSGenre | CMSTag
  ) => {
    return !!formValues.find(
      (value) => value?.getUniqueId() === entity?.getUniqueId()
    );
  };

  const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  return (
    <TableWrapper style={style}>
      <TableSlice style={{ marginRight: "0.5%" }}>
        <TableSliceHeader>
          {`Selected ${label} [${selectedValues.length || 0}]`}
        </TableSliceHeader>
        <TableSliceContent>
          {selectedValues.map(
            (
              entity: BaseEntity | CMSAsset | CMSGenre | CMSTag,
              index: number
            ) => (
              <EntityRow
                key={entity?.getUniqueId() + "key2" + index}
                entity={entity}
                onClick={() => remove(index)}
                rightSide={false}
                noImages={noImages}
              />
            )
          )}
        </TableSliceContent>
      </TableSlice>
      <TableSlice>
        <TableSliceHeader>
          {capitalizeFirstLetter(label)}
          <CustomSearchBar onSearch={searchFunc} />
        </TableSliceHeader>
        <TableSliceContent>
          {!!values.length &&
            values.map((entity, index) => (
              <EntityRow
                key={entity.getUniqueId() + "key" + index}
                isDisabled={isSelected(selectedValues, entity)}
                noImages={noImages}
                entity={entity}
                onClick={() => push(entity)}
              />
            ))}
        </TableSliceContent>
      </TableSlice>
    </TableWrapper>
  );
};
